import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type Destinatario, type FileEntry } from './api';
import { enLote, type ResultadoLote } from './seleccion';
import { Campo, Modal } from './ui';

export function RenombrarDialog({
  entry,
  carpeta,
  onClose,
  onListo,
}: {
  entry: FileEntry;
  carpeta: string;
  onClose: () => void;
  onListo: () => void;
}) {
  const [nombre, setNombre] = useState(entry.name);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function guardar() {
    if (!nombre.trim() || nombre.trim() === entry.name) return onClose();
    setEnviando(true);
    setError(null);

    try {
      await api.move(entry.path, carpeta + nombre.trim() + (entry.kind === 'folder' ? '/' : ''));
      onListo();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos renombrar.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Renombrar"
      subtitulo={entry.name}
      onClose={onClose}
      acciones={
        <>
          <button className="secundario" onClick={onClose}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Campo id="nuevo-nombre" etiqueta="Nombre">
        <input
          id="nuevo-nombre"
          value={nombre}
          autoFocus
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && guardar()}
        />
      </Campo>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

export function MoverDialog({
  entries,
  carpetas,
  onClose,
  onListo,
}: {
  entries: FileEntry[];
  carpetas: string[];
  onClose: () => void;
  /** Con lo que se movió y lo que no, para que el que llama informe. */
  onListo: (r: ResultadoLote<FileEntry>) => void;
}) {
  const [destino, setDestino] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Una carpeta no puede ser destino de sí misma.
  const movidas = new Set(entries.map((e) => e.path));
  const opciones = carpetas.filter((c) => !movidas.has(c));

  async function mover() {
    setEnviando(true);
    setError(null);

    const r = await enLote(entries, (e) =>
      api.move(e.path, destino + e.name + (e.kind === 'folder' ? '/' : '')),
    );

    // Con uno solo, el error se muestra acá mismo, como antes.
    if (entries.length === 1 && r.fallidos.length === 1) {
      setError(r.fallidos[0].motivo);
      setEnviando(false);
      return;
    }
    onListo(r);
  }

  return (
    <Modal
      titulo="Mover a"
      subtitulo={entries.length === 1 ? entries[0].name : `${entries.length} elementos`}
      onClose={onClose}
      acciones={
        <>
          <button className="secundario" onClick={onClose}>
            Cancelar
          </button>
          <button onClick={mover} disabled={enviando}>
            {enviando ? 'Moviendo…' : 'Mover acá'}
          </button>
        </>
      }
    >
      <Campo id="destino" etiqueta="Carpeta destino" ayuda="También podés arrastrar el archivo sobre una carpeta.">
        <select id="destino" value={destino} onChange={(e) => setDestino(e.target.value)}>
          <option value="">Mis archivos (raíz)</option>
          {opciones.map((c) => (
            <option key={c} value={c}>
              {c.replace(/\/$/, '')}
            </option>
          ))}
        </select>
      </Campo>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

export function CompartirDialog({
  paths,
  nombre,
  onClose,
  onListo,
}: {
  paths: string[];
  nombre: string;
  onClose: () => void;
  onListo: (mensaje: string) => void;
}) {
  const [destinatarios, setDestinatarios] = useState<Destinatario[]>([]);
  const [elegidos, setElegidos] = useState<Destinatario[]>([]);
  const [permiso, setPermiso] = useState<'read' | 'write'>('read');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api
      .recipients()
      .then(setDestinatarios)
      .catch(() => setError('No pudimos cargar la lista de usuarios.'));
  }, []);

  async function compartir() {
    if (elegidos.length === 0) return;
    setEnviando(true);
    setError(null);

    try {
      const { created, notified, fallidos } = await api.share(
        paths,
        elegidos.map((d) => d.id),
        permiso,
      );
      const varios = paths.length > 1;
      const base =
        created === 0
          ? `Ya estaba${varios ? 'n compartidos' : ' compartido'}; se actualizó el permiso.`
          : varios
            ? `Se crearon ${created} compartidos${notified > 0 ? ` y se avisó por correo a ${notified} ${notified === 1 ? 'persona' : 'personas'}.` : '.'}`
            : `Compartido con ${created} usuario${created > 1 ? 's' : ''}${notified > 0 ? ' y avisado por correo.' : '.'}`;
      onListo(fallidos.length > 0 ? `${base} No se pudieron compartir ${fallidos.length}.` : base);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos compartir.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Compartir"
      subtitulo={nombre}
      onClose={onClose}
      acciones={
        <>
          <button className="secundario" onClick={onClose}>
            Cancelar
          </button>
          <button onClick={compartir} disabled={enviando || elegidos.length === 0}>
            {enviando ? 'Compartiendo…' : 'Compartir'}
          </button>
        </>
      }
    >
      <BuscadorUsuarios
        opciones={destinatarios}
        elegidos={elegidos}
        onCambio={setElegidos}
      />

      <fieldset>
        <legend>Permiso</legend>
        <label className="fila-check">
          <input
            type="radio"
            id="permiso-read"
            name="permiso"
            checked={permiso === 'read'}
            onChange={() => setPermiso('read')}
          />
          Solo lectura
        </label>
        <label className="fila-check">
          <input
            type="radio"
            id="permiso-write"
            name="permiso"
            checked={permiso === 'write'}
            onChange={() => setPermiso('write')}
          />
          Lectura y edición
        </label>
      </fieldset>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

export function LinkDialog({
  path,
  nombre,
  onClose,
  onListo,
}: {
  path: string;
  nombre: string;
  onClose: () => void;
  onListo: (url: string) => void;
}) {
  const enUnaSemana = new Date();
  enUnaSemana.setDate(enUnaSemana.getDate() + 7);

  const [vence, setVence] = useState(enUnaSemana.toISOString().slice(0, 10));
  const [conTope, setConTope] = useState(false);
  const [limite, setLimite] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function crear() {
    setEnviando(true);
    setError(null);

    try {
      // Vence al final del día elegido, no a las 00:00.
      const link = await api.createPublicLink(path, `${vence}T23:59:59`, conTope ? limite : null);
      onListo(link.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos crear el link.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Link público"
      subtitulo={nombre}
      onClose={onClose}
      acciones={
        <>
          <button className="secundario" onClick={onClose}>
            Cancelar
          </button>
          <button onClick={crear} disabled={enviando || !vence}>
            {enviando ? 'Creando…' : 'Crear link'}
          </button>
        </>
      }
    >
      <p className="aviso">Cualquiera con el link puede descargar el archivo sin iniciar sesión.</p>

      <Campo id="vence" etiqueta="Vence el">
        <input
          id="vence"
          type="date"
          required
          value={vence}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setVence(e.target.value)}
        />
      </Campo>

      <label className="fila-check">
        <input
          type="checkbox"
          id="con-tope"
          checked={conTope}
          onChange={(e) => setConTope(e.target.checked)}
        />
        Limitar la cantidad de descargas
      </label>

      {conTope && (
        <Campo id="limite" etiqueta="Máximo de descargas">
          <input
            id="limite"
            type="number"
            min={1}
            max={10000}
            value={limite}
            onChange={(e) => setLimite(Number(e.target.value))}
          />
        </Campo>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/** "Julián" y "julian" tienen que coincidir: se compara sin acentos ni mayúsculas. */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const MAX_SUGERENCIAS = 8;

/**
 * Buscador de personas para compartir. Con cien usuarios, una lista de
 * casillas obligaba a recorrerla entera; acá se escribe y se elige. Los
 * elegidos quedan como etiquetas arriba del campo.
 */
function BuscadorUsuarios({
  opciones,
  elegidos,
  onCambio,
}: {
  opciones: Destinatario[];
  elegidos: Destinatario[];
  onCambio: (d: Destinatario[]) => void;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const sugerencias = useMemo(() => {
    const termino = normalizar(texto.trim());
    if (!termino) return [];
    const tomados = new Set(elegidos.map((e) => e.id));
    // Primero los que empiezan con lo escrito (en el nombre o en el apellido),
    // después los que lo contienen en cualquier parte.
    const candidatos = opciones.filter((o) => !tomados.has(o.id));
    const empiezan = candidatos.filter((o) =>
      normalizar(o.displayName).split(/\s+/).some((p) => p.startsWith(termino)),
    );
    const contienen = candidatos.filter(
      (o) => !empiezan.includes(o) && normalizar(`${o.displayName} ${o.detalle ?? ''}`).includes(termino),
    );
    return [...empiezan, ...contienen].slice(0, MAX_SUGERENCIAS);
  }, [texto, opciones, elegidos]);

  function elegir(d: Destinatario) {
    onCambio([...elegidos, d]);
    setTexto('');
    setActivo(0);
    input.current?.focus();
  }

  function quitar(id: number) {
    onCambio(elegidos.filter((e) => e.id !== id));
    input.current?.focus();
  }

  function teclado(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && sugerencias.length > 0) {
      e.preventDefault();
      setAbierto(true);
      setActivo((a) => (a + 1) % sugerencias.length);
    } else if (e.key === 'ArrowUp' && sugerencias.length > 0) {
      e.preventDefault();
      setActivo((a) => (a - 1 + sugerencias.length) % sugerencias.length);
    } else if (e.key === 'Enter' && abierto && sugerencias[activo]) {
      e.preventDefault();
      elegir(sugerencias[activo]);
    } else if (e.key === 'Escape' && abierto && texto) {
      // Escape cierra las sugerencias; recién el segundo cierra el diálogo.
      e.stopPropagation();
      setAbierto(false);
    } else if (e.key === 'Backspace' && !texto && elegidos.length > 0) {
      quitar(elegidos[elegidos.length - 1].id);
    }
  }

  const visible = abierto && sugerencias.length > 0;
  const sinResultados = abierto && texto.trim() !== '' && sugerencias.length === 0;

  return (
    <div className="buscador-usuarios">
      <label htmlFor="buscar-usuario" className="etiqueta-campo">
        Con quién
      </label>
      <div className="campo-etiquetas" onClick={() => input.current?.focus()}>
        {elegidos.map((d) => (
          <span key={d.id} className="etiqueta-usuario">
            {d.displayName}
            {d.detalle && <span className="detalle"> · {d.detalle}</span>}
            <button
              type="button"
              onClick={() => quitar(d.id)}
              aria-label={`Quitar a ${d.displayName}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={input}
          id="buscar-usuario"
          role="combobox"
          aria-expanded={visible}
          aria-controls="sugerencias-usuarios"
          aria-activedescendant={visible ? `sugerencia-${sugerencias[activo]?.id}` : undefined}
          autoComplete="off"
          autoFocus
          placeholder={elegidos.length === 0 ? 'Escribí un nombre…' : ''}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setActivo(0);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          // El retraso deja que el clic en una sugerencia llegue antes de cerrar.
          onBlur={() => setTimeout(() => setAbierto(false), 150)}
          onKeyDown={teclado}
        />
      </div>

      {visible && (
        <ul id="sugerencias-usuarios" role="listbox" className="sugerencias">
          {sugerencias.map((d, i) => (
            <li
              key={d.id}
              id={`sugerencia-${d.id}`}
              role="option"
              aria-selected={i === activo}
              className={i === activo ? 'activa' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActivo(i)}
              onClick={() => elegir(d)}
            >
              {d.displayName}
              {d.detalle && <span className="detalle">{d.detalle}</span>}
            </li>
          ))}
        </ul>
      )}
      {sinResultados && <p className="vacio-chico">Nadie coincide con "{texto.trim()}".</p>}
      {opciones.length === 0 && <p className="vacio-chico">No hay otros usuarios.</p>}
    </div>
  );
}
